import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scan, Calendar, Eye, ArrowLeft, Loader2, Share2, Check, Clock, Tag } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  tutorial: 'Tutorial',
  news: 'Noticias IA',
  'use-case': 'Caso de uso',
  update: 'Novedad',
};

const CATEGORY_COLORS = {
  tutorial: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  news: 'bg-violet-500/15 text-violet-400 border-violet-500/25',
  'use-case': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  update: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
};

// ─── Inline markdown formatter ─────────────────────────────────────────────────
function inlineFormat(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="text-zinc-200 italic">$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-zinc-800/80 text-blue-300 px-1.5 py-0.5 rounded-md text-[0.85em] font-mono border border-zinc-700/50">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors" target="_blank" rel="noopener noreferrer">$1</a>');
}

// ─── Markdown renderer ─────────────────────────────────────────────────────────
function RenderContent({ content }) {
  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Headings
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={i} className="font-heading text-3xl sm:text-4xl font-black text-white mt-10 mb-5 leading-tight">
          {line.slice(2)}
        </h1>
      );
    } else if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="font-heading text-2xl sm:text-3xl font-bold text-white mt-10 mb-4 leading-snug relative">
          <span className="absolute -left-4 text-blue-500 font-mono text-lg opacity-60 hidden sm:block">#</span>
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="font-heading text-xl font-bold text-zinc-100 mt-7 mb-3 leading-snug">
          {line.slice(4)}
        </h3>
      );

    // Blockquote
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="relative my-6 pl-5 border-l-[3px] border-blue-500">
          <div className="absolute -left-3 top-0 text-blue-500 text-3xl leading-none opacity-40">"</div>
          <p className="text-zinc-300 text-lg italic leading-relaxed"
            dangerouslySetInnerHTML={{ __html: inlineFormat(line.slice(2)) }} />
        </blockquote>
      );

    // YouTube embed
    } else if (/^\[youtube:([\w-]{11})\]$/.test(line)) {
      const ytId = line.match(/^\[youtube:([\w-]{11})\]$/)[1];
      elements.push(
        <div key={i} className="my-8 -mx-2 sm:mx-0 rounded-xl overflow-hidden border border-zinc-800/60 bg-black" style={{ aspectRatio: '16/9' }}>
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            style={{ border: 'none' }}
          />
        </div>
      );

    // Image
    } else if (/^!\[([^\]]*)\]\(([^)]+)\)$/.test(line)) {
      const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      elements.push(
        <figure key={i} className="my-8 -mx-2 sm:mx-0">
          <div className="overflow-hidden rounded-xl border border-zinc-800/60 bg-zinc-900">
            <img
              src={imgMatch[2]}
              alt={imgMatch[1]}
              className="w-full object-cover max-h-[520px]"
              loading="lazy"
            />
          </div>
          {imgMatch[1] && imgMatch[1] !== 'imagen' && (
            <figcaption className="text-center text-zinc-500 text-xs font-mono mt-3 leading-relaxed">
              {imgMatch[1]}
            </figcaption>
          )}
        </figure>
      );

    // Code block
    } else if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <div key={i} className="my-6 rounded-xl overflow-hidden border border-zinc-700/60 bg-zinc-900">
          {lang && (
            <div className="flex items-center justify-between bg-zinc-800/80 px-4 py-2.5 border-b border-zinc-700/60">
              <span className="text-xs font-mono text-zinc-400 uppercase tracking-wider">{lang}</span>
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-600" />
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-600" />
                <div className="w-2.5 h-2.5 rounded-full bg-zinc-600" />
              </div>
            </div>
          )}
          <pre className="p-4 sm:p-5 overflow-x-auto text-sm font-mono text-zinc-300 leading-relaxed">
            <code>{codeLines.join('\n')}</code>
          </pre>
        </div>
      );

    // Unordered list
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={i} className="my-5 space-y-2.5">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-3 text-zinc-300 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
            </li>
          ))}
        </ul>
      );
      continue;

    // Ordered list
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(
        <ol key={i} className="my-5 space-y-2.5">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-3 text-zinc-300 leading-relaxed">
              <span className="font-mono text-sm text-blue-400 font-bold flex-shrink-0 mt-0.5 w-5">{j + 1}.</span>
              <span dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
            </li>
          ))}
        </ol>
      );
      continue;

    // Divider
    } else if (line.startsWith('---')) {
      elements.push(
        <div key={i} className="my-10 flex items-center gap-4">
          <div className="flex-1 h-px bg-zinc-800" />
          <div className="flex gap-1">
            <div className="w-1 h-1 rounded-full bg-zinc-700" />
            <div className="w-1 h-1 rounded-full bg-zinc-700" />
            <div className="w-1 h-1 rounded-full bg-zinc-700" />
          </div>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
      );

    // Empty line
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-3" />);

    // Paragraph
    } else {
      elements.push(
        <p key={i}
          className="text-zinc-300 leading-[1.85] text-base sm:text-[1.05rem] my-3"
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      );
    }
    i++;
  }

  return <div className="sm:pl-0">{elements}</div>;
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function BlogPost({ slug, onBack, onBlog, user }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    fetchPost();
    return () => {
      document.title = 'Veritas Scan | Detector de Contenido IA';
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', 'Detecta si una imagen, texto o vídeo fue generado por IA.');
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = 'https://www.veritas-scan.com/';
      document.getElementById('blog-schema')?.remove();
    };
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/blog/posts/${slug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPost(res.data);

      // SEO
      document.title = `${res.data.title} — Veritas Scan`;
      const setMeta = (sel, val, attr = 'content') => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute(attr, val);
      };
      setMeta('meta[name="description"]', res.data.excerpt);

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
      canonical.href = `https://www.veritas-scan.com/blog/${res.data.slug}`;

      const setOg = (prop, val) => {
        let el = document.querySelector(`meta[property="${prop}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
        el.setAttribute('content', val);
      };
      setOg('og:title', `${res.data.title} — Veritas Scan`);
      setOg('og:description', res.data.excerpt);
      setOg('og:url', `https://www.veritas-scan.com/blog/${res.data.slug}`);
      setOg('og:type', 'article');

      let schema = document.getElementById('blog-schema');
      if (!schema) { schema = document.createElement('script'); schema.id = 'blog-schema'; schema.type = 'application/ld+json'; document.head.appendChild(schema); }
      schema.textContent = JSON.stringify({
        '@context': 'https://schema.org', '@type': 'Article',
        headline: res.data.title, description: res.data.excerpt,
        url: `https://www.veritas-scan.com/blog/${res.data.slug}`,
        datePublished: res.data.created_at, dateModified: res.data.updated_at || res.data.created_at,
        author: { '@type': 'Organization', name: 'Veritas Scan', url: 'https://www.veritas-scan.com' },
        publisher: { '@type': 'Organization', name: 'Veritas Scan', url: 'https://www.veritas-scan.com' },
        keywords: (res.data.tags || []).join(', '), inLanguage: 'es',
      });
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}/blog/${slug}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );

  if (notFound || !post) return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4 px-6">
      <p className="text-6xl">🔍</p>
      <h1 className="font-heading text-2xl font-black text-white text-center">Post no encontrado</h1>
      <button onClick={onBlog} className="text-blue-400 hover:text-blue-300 font-mono text-sm transition-colors">← Volver al blog</button>
    </div>
  );

  const date = new Date(post.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  const readTime = Math.max(1, Math.ceil((post.content || '').split(/\s+/).length / 200));
  const categoryColor = CATEGORY_COLORS[post.category] || 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30';

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(ellipse, #3b82f6, transparent 70%)' }} />
      </div>

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-zinc-950/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          {/* Back */}
          <button
            onClick={onBlog}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-mono text-xs sm:text-sm group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden sm:inline">Blog</span>
          </button>

          <div className="w-px h-4 bg-zinc-800" />

          {/* Logo */}
          <button onClick={onBack} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Scan className="w-4 h-4 text-blue-500" />
            <span className="font-heading text-sm font-black tracking-wide">
              VERITAS <span className="text-blue-500">SCAN</span>
            </span>
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-700/80 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-xs transition-all"
          >
            {copied
              ? <><Check className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">Copiado</span></>
              : <><Share2 className="w-3 h-3" />Compartir</>
            }
          </button>
        </div>
      </header>

      {/* ── ARTICLE ── */}
      <main className="relative z-10">
        <motion.article
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* ── HERO ── */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-10">

            {/* Category + meta row */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className={`text-xs font-mono px-2.5 py-1 rounded-full border font-medium ${categoryColor}`}>
                {CATEGORY_LABELS[post.category] || post.category}
              </span>
              {!post.published && (
                <span className="text-xs font-mono px-2.5 py-1 rounded-full border bg-red-500/15 text-red-400 border-red-500/25">
                  Borrador
                </span>
              )}
              <div className="flex items-center gap-3 text-zinc-500 text-xs font-mono ml-auto">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />{readTime} min
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />{post.views || 0}
                </span>
              </div>
            </div>

            {/* Cover image or emoji */}
            {post.cover_image ? (
              <div className="mb-6 -mx-4 sm:mx-0 rounded-none sm:rounded-2xl overflow-hidden border-y sm:border border-zinc-800/60">
                <img
                  src={post.cover_image}
                  alt={post.title}
                  className="w-full max-h-72 sm:max-h-96 object-cover"
                  loading="eager"
                />
              </div>
            ) : (
              <div className="text-6xl sm:text-7xl mb-6 leading-none">{post.cover_emoji || '📝'}</div>
            )}

            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-white leading-[1.1] tracking-tight mb-5">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-zinc-400 text-lg sm:text-xl leading-relaxed mb-8">
              {post.excerpt}
            </p>

            {/* Author + date */}
            <div className="flex items-center gap-3 pb-8 border-b border-zinc-800/80">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {post.author?.[0]?.toUpperCase() || 'V'}
              </div>
              <div className="min-w-0">
                <p className="text-zinc-200 text-sm font-medium truncate">{post.author || 'Veritas Scan'}</p>
                <p className="text-zinc-500 text-xs font-mono flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />{date}
                </p>
              </div>
            </div>
          </div>

          {/* ── CONTENT ── */}
          <div className="max-w-3xl mx-auto px-4 sm:px-6 sm:pl-10 pb-16">
            <div className="text-base">
              <RenderContent content={post.content} />
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="mt-12 pt-8 border-t border-zinc-800/80">
                <div className="flex flex-wrap items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-zinc-600" />
                  {post.tags.map(tag => (
                    <span key={tag} className="text-xs font-mono px-2.5 py-1 bg-zinc-900 text-zinc-500 rounded-full border border-zinc-800 hover:border-zinc-600 hover:text-zinc-300 transition-colors cursor-default">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 p-6 sm:p-8 rounded-2xl border border-zinc-800/60 bg-gradient-to-br from-zinc-900 to-zinc-900/50 text-center space-y-4">
              <div className="text-3xl">🔍</div>
              <div>
                <p className="text-white font-heading text-xl font-bold mb-1">¿Quieres detectar contenido IA?</p>
                <p className="text-zinc-400 text-sm">Prueba Veritas Scan gratis — sin registro.</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={onBack}
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm font-medium rounded-xl transition-all"
                >
                  Probar ahora →
                </button>
                <button
                  onClick={onBlog}
                  className="w-full sm:w-auto px-6 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all"
                >
                  Más artículos
                </button>
              </div>
            </div>
          </div>
        </motion.article>
      </main>
    </div>
  );
}
